import { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { supabase } from '../lib/supabase';
import { useAuditLog } from './useAuditLog';

export interface LockRequest {
  id: number;
  created_at: string;
  updated_at: string;
  device_id: string;
  finance_user_id: number;
  request_type: 'LOCK' | 'UNLOCK';
  reason: string;
  status: 'PENDING' | 'APPROVED' | 'DENIED';
  admin_id?: number;
  admin_response?: string;
  approved_at?: string;
  intune_instructions?: string;
  // Joined fields from related tables
  device?: {
    serial_number: string;
    device_name: string;
    owner_name: string;
    is_locked: boolean;
  };
  finance_user?: {
    email: string;
    firstname: string;
    lastname: string;
  };
  admin?: {
    email: string;
    firstname: string;
    lastname: string;
  };
}

export function useLockRequests() {
  const { user } = useAuth0();
  const { logAction } = useAuditLog();
  const [lockRequests, setLockRequests] = useState<LockRequest[]>([]);
  const [pendingRequests, setPendingRequests] = useState<LockRequest[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchLockRequests = async (financeUserOnly = false) => {
    setLoading(true);
    try {
      let query = supabase
        .from('lock_requests')
        .select(`
          *,
          device:devices!lock_requests_device_id_fkey (
            serial_number,
            device_name,
            owner_name,
            is_locked
          ),
          finance_user:finance_users!lock_requests_finance_user_id_fkey (
            email,
            firstname,
            lastname
          ),
          admin:admins!lock_requests_admin_id_fkey (
            email,
            firstname,
            lastname
          )
        `)
        .order('created_at', { ascending: false });

      if (financeUserOnly) {
        // Get requests for current finance user
        const { data: financeUser } = await supabase
          .from('finance_users')
          .select('id')
          .eq('auth0id', user?.sub)
          .single();
        
        if (financeUser) {
          query = query.eq('finance_user_id', financeUser.id);
        }
      }

      const { data, error } = await query;

      if (error) throw error;

      setLockRequests(data || []);
      setPendingRequests((data || []).filter(req => req.status === 'PENDING'));
    } catch (error) {
      console.error('Error fetching lock requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const createLockRequest = async (
    deviceId: string,
    requestType: 'LOCK' | 'UNLOCK',
    reason: string
  ) => {
    if (!user?.sub) throw new Error('User not authenticated');

    try {
      // Get finance user ID
      const { data: financeUser, error: userError } = await supabase
        .from('finance_users')
        .select('id')
        .eq('auth0id', user.sub)
        .single();

      if (userError || !financeUser) throw new Error('Finance user not found');

      // Get device info for the request
      const { data: device, error: deviceError } = await supabase
        .from('devices')
        .select('serial_number, device_name, owner_name')
        .eq('id', deviceId)
        .single();

      if (deviceError) throw deviceError;

      // Create the lock request
      const { error } = await supabase
        .from('lock_requests')
        .insert({
          device_id: deviceId,
          finance_user_id: financeUser.id,
          request_type: requestType,
          reason: reason,
          intune_instructions: requestType === 'LOCK' 
            ? `Add the user to 1914 - Locked by ICT static group`
            : `Remove the user from the above mentioned\nAdd the user to 1914 - Unlocked by ICT Group`
        });

      if (error) throw error;

      // Log the action
      await logAction(
        'create_lock_request',
        'device_management',
        {
          resourceType: 'device',
          resourceId: deviceId,
          details: {
            request_type: requestType,
            reason: reason,
            device_serial: device?.serial_number,
            device_name: device?.device_name,
            owner_name: device?.owner_name
          },
          success: true
        }
      );

      await fetchLockRequests(true);
    } catch (error) {
      console.error('Error creating lock request:', error);
      throw error;
    }
  };

  const updateLockRequestStatus = async (
    requestId: number,
    status: 'APPROVED' | 'DENIED'
  ) => {
    if (!user?.sub) throw new Error('User not authenticated');

    try {
      // Get admin ID
      const { data: admin, error: adminError } = await supabase
        .from('admins')
        .select('id')
        .eq('auth0ID', user.sub)
        .single();

      if (adminError || !admin) throw new Error('Admin not found');

      // Get the request details before updating
      const { data: request, error: requestError } = await supabase
        .from('lock_requests')
        .select(`
          *,
          device:devices!lock_requests_device_id_fkey (
            serial_number,
            device_name,
            is_locked
          )
        `)
        .eq('id', requestId)
        .single();

      if (requestError || !request) throw new Error('Request not found');

      // Update the request status
      const updateData: any = {
        status,
        admin_id: admin.id,
        approved_at: status === 'APPROVED' ? new Date().toISOString() : null
      };

      const { error } = await supabase
        .from('lock_requests')
        .update(updateData)
        .eq('id', requestId);

      if (error) throw error;

      // If approved, update the device status
      if (status === 'APPROVED') {
        const newLockStatus = request.request_type === 'LOCK';

        let defaultLockMessageId: number | null = null;
        if (newLockStatus) {
          const { data: defaultLockMessage } = await supabase
            .from('lock_messages')
            .select('id')
            .eq('is_default', true)
            .maybeSingle();

          defaultLockMessageId = defaultLockMessage?.id ?? null;
        }
        
        // Update device status and add the finance reason to the note
        const { error: deviceError } = await supabase
          .from('devices')
          .update({ 
            is_locked: newLockStatus,
            note: request.reason,
            lock_message_id: newLockStatus ? defaultLockMessageId : null
          })
          .eq('id', request.device_id);

        if (deviceError) throw deviceError;

        // Log the device status change
        await logAction(
          request.request_type === 'LOCK' ? 'lock_device' : 'unlock_device',
          'device_management',
          {
            resourceType: 'device',
            resourceId: request.device_id,
            details: {
              device_serial: request.device?.serial_number,
              device_name: request.device?.device_name,
              previous_status: request.device?.is_locked ? 'locked' : 'unlocked',
              new_status: newLockStatus ? 'locked' : 'unlocked',
              triggered_by: 'finance_request',
              request_id: requestId
            },
            success: true
          }
        );
      }

      // Log the admin action
      await logAction(
        status === 'APPROVED' ? 'approve_lock_request' : 'deny_lock_request',
        'admin_management',
        {
          resourceType: 'lock_request',
          resourceId: requestId.toString(),
          details: {
            request_type: request.request_type,
            device_serial: request.device?.serial_number
          },
          success: true
        }
      );

      await fetchLockRequests();
    } catch (error) {
      console.error('Error updating lock request:', error);
      throw error;
    }
  };

  return {
    lockRequests,
    pendingRequests,
    loading,
    fetchLockRequests,
    createLockRequest,
    updateLockRequestStatus
  };
}
